import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useMediaQuery } from "@amphore-dev/amphore-lib";
import { EventClickArg, EventContentArg } from "@fullcalendar/core/index.js";
import { DateClickArg } from "@fullcalendar/interaction/index.js";
import FullCalendar from "@fullcalendar/react";
import { endOfMonth, format, isWithinInterval, parseISO } from "date-fns";

import { useQuery } from "@tanstack/react-query";

import ProjectsService from "@/services/ProjectsService";
import ViewsService from "@/services/ViewsService";

import { useActiveProject } from "@/hooks/useActiveProject";
import { useActiveView } from "@/hooks/useActiveView";
import { useHomeNavigation } from "@/hooks/useHomeNavigation";
import { useTasksFilters } from "@/hooks/useTasksFilters";

import {
	Calendar,
	CalendarDayBar,
	CalendarEvent,
} from "@/components/molecules";

import { TDaySummary } from "@/utils/UTasks";

import { TABLET_MEDIA_QUERY } from "@/constants/CConstants";
import { taskKeys } from "@/constants/CQueryKeys";

import { TDatesFilter, TTask } from "@/types";

import {
	getDisplayedMonthRange,
	parseDay,
	summarizeEventsByDay,
	tasksToCalendarEvents,
} from "@/utils";

/** What `summarizeEventsByDay` hung on a compact day's event. */
const summaryOf = (arg: EventContentArg) =>
	arg.event.extendedProps.summary as TDaySummary;

export const HomeCalendar: React.FC = () => {
	const calendarRef = useRef<FullCalendar>(null);
	// Too narrow for a dot per task: one bar per day instead.
	const compact = useMediaQuery(TABLET_MEDIA_QUERY);
	const { view: activeView } = useActiveView();
	const { project: activeProject } = useActiveProject();
	const { toTask } = useHomeNavigation();
	const { filters, setFilters } = useTasksFilters();
	const [currentRange, setCurrentRange] = useState<TDatesFilter>(
		getDisplayedMonthRange(
			filters.dateFrom ? parseDay(filters.dateFrom) : new Date()
		)
	);

	const handleDateClick = useCallback(
		(date: DateClickArg) => {
			const clickedDate = date.date;

			if (currentRange.dateFrom && currentRange.dateTo) {
				const isInCurrentRange = isWithinInterval(clickedDate, {
					start: parseISO(currentRange.dateFrom),
					end: parseISO(currentRange.dateTo),
				});

				if (!isInCurrentRange) {
					const newRange = getDisplayedMonthRange(clickedDate);
					setCurrentRange(newRange);
				}
			}

			document
				.querySelector(".fc-daygrid-day.active")
				?.classList.remove("active");
			document
				.querySelector(
					'[data-date="' + format(date.date, "yyyy-MM-dd") + '"]'
				)
				?.classList.add("active");

			setFilters({
				dateFrom: format(clickedDate, "yyyy-MM-dd"),
				dateTo: format(clickedDate, "yyyy-MM-dd"),
			});
		},
		[currentRange, setFilters]
	);

	const fetchTasks = () => {
		const payload = {
			...filters,
			...currentRange,
			limit: 0,
		};

		if (!activeView) return [];
		if (activeProject) {
			// fetch tasks for the active project and calendar
			return ProjectsService.tasksOfProject(activeProject.id, {
				...payload,
				projectId: activeProject.id,
			});
		}
		// fetch tasks for the active calendar
		return ViewsService.getViewTasks(activeView.id, {
			...payload,
			viewId: activeView.id,
		});
	};

	// The panel moves the period to the task's days if it has to.
	const handleEventClick = (event: EventClickArg) => {
		const task = event.event.extendedProps as TTask;

		toTask(task.id);
	};

	const handleNavigate = useCallback(
		(_action: unknown, date: { start: Date }) => {
			const monthRange = getDisplayedMonthRange(date.start);
			setFilters({
				dateFrom: format(date.start, "yyyy-MM-dd"),
				dateTo: format(endOfMonth(date.start), "yyyy-MM-dd"),
			});
			setCurrentRange(monthRange);
			document
				.querySelector(".fc-daygrid-day.active")
				?.classList.remove("active");
		},
		[setFilters]
	);

	const { data, isLoading } = useQuery({
		queryKey: taskKeys.ofPeriod(
			activeView?.id,
			activeProject?.id,
			currentRange.dateFrom,
			currentRange.dateTo
		),
		queryFn: () => fetchTasks(),
		enabled: !!activeView,
	});

	const events = useMemo(() => {
		if (!data) return [];
		const taskEvents = tasksToCalendarEvents(data.items);
		return compact ? summarizeEventsByDay(taskEvents) : taskEvents;
	}, [data, compact]);

	useEffect(() => {
		calendarRef.current
			?.getApi()
			.gotoDate(
				filters.dateFrom ? parseDay(filters.dateFrom) : new Date()
			);
		const dates = getDisplayedMonthRange(
			filters.dateFrom ? parseDay(filters.dateFrom) : new Date()
		);

		if (
			filters.dateFrom &&
			filters.dateTo &&
			filters.dateFrom === filters.dateTo
		) {
			calendarRef.current?.getApi().trigger("dateClick", {
				date: parseDay(filters.dateFrom),
			} as DateClickArg);
		} else {
			setCurrentRange(dates);
		}
	}, [filters.dateFrom, filters.dateTo]);

	return (
		<div className="z-0" data-tour="calendar">
			<Calendar
				calendarRef={calendarRef}
				isLoading={isLoading}
				tasks={events}
				compact={compact}
				dateClick={(e: DateClickArg) => {
					handleDateClick(e);
				}}
				eventContent={(props) =>
					compact ? (
						<CalendarDayBar {...summaryOf(props)} />
					) : (
						<CalendarEvent {...props} onClick={handleEventClick} />
					)
				}
				eventClick={handleEventClick}
				eventDisplay={compact ? "block" : "list-item"}
				onNavigate={handleNavigate}
			/>
		</div>
	);
};
